import { useAuth } from '@clerk/react'
import { type BootPhase, BootPhaseEnum } from '@schemas/boot'
import { m, useReducedMotion } from 'motion/react'
import { useMemo, useSyncExternalStore } from 'react'
import { generateReference } from '../../lib/reference.ts'
import { router } from '../../router.tsx'
import { ErrorPage } from '../error-pages/error-page.tsx'
import { BOOT_RULE_DURATION } from './emission.ts'
import { EmissionMark, IndeterminateRule } from './rally-loader.tsx'
import { BOOT_EXIT_MS, bootLabel, useAppBoot } from './use-app-boot.ts'

const LOCKUP_MARK_HEIGHT = 84

// Corner and footer strips are context, not content: below 900px they drop and only the lockup
// and status block remain.
const stripClass = 'hidden min-[900px]:flex items-center gap-6 font-mono text-[10px] text-ink-meta'

export interface AppBootScreenProps {
  phase: BootPhase
  /** What is being restored. Defaults to the cold-start case. */
  label?: string
  onRetry?: () => void
}

// RAL-183. Full viewport on the void ground, carrying the same 48px ruled grid as the canvas
// (DESIGN_LANGUAGE §3) — the boot screen is not exempt from it; the grid is what makes an empty
// page read as engineered rather than broken.
//
// Rendered as a fixed overlay rather than a gate, so the shell mounts underneath while this
// fades out. Two entrances stacked read as a stutter.
export function AppBootScreen({ phase, label = 'Loading workspace', onRetry }: AppBootScreenProps) {
  const reduce = useReducedMotion()
  const reference = useMemo(() => generateReference(), [])

  if (phase === BootPhaseEnum.enum.idle) return null

  if (phase === BootPhaseEnum.enum.failed) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-ground-void px-6 py-10">
        <ErrorPage
          variant="shell"
          failureClass="server"
          publisherStatus={{ state: 'unknown' }}
          reference={reference}
          onRetry={onRetry ?? (() => window.location.reload())}
        />
      </div>
    )
  }

  const exiting = phase === BootPhaseEnum.enum.exiting

  return (
    <m.div
      role="status"
      className="bg-blueprint fixed inset-0 z-50 flex flex-col bg-ground-void p-[26px]"
      animate={{ opacity: exiting ? 0 : 1 }}
      // Opacity only. The boot screen is a substrate, not a card: no scale, no slide, no blur.
      transition={{ duration: exiting && !reduce ? BOOT_EXIT_MS / 1000 : 0, ease: 'linear' }}
    >
      <div className={`${stripClass} justify-between`}>
        <span>Rally · creator_ops · v0.4</span>
        <span>session · restoring</span>
      </div>

      {/* flex-1 rather than the outer justify-between: below 900px the strips are display:none,
          and space-between with a single visible child would top-align the lockup. */}
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="flex items-center gap-[26px]">
          <EmissionMark size="lg" height={LOCKUP_MARK_HEIGHT} ink="var(--color-ink-primary)" />
          {/* Live text, never a path, and it never animates. */}
          <span className="font-display text-[64px] font-extrabold uppercase leading-[0.9] tracking-[-0.015em] text-ink-primary [font-stretch:118%]">
            Rally
          </span>
        </div>

        <div className="mt-[52px] w-[420px] max-w-full">
          <IndeterminateRule duration={BOOT_RULE_DURATION} />
          <div className="mt-3 flex items-center gap-2 font-mono text-[10px] text-ink-meta">
            <span className="uppercase tracking-[0.13em]">{label}</span>
            {/* The blink's reduced-motion guard lives in globals.css with the keyframe, not here. */}
            <span aria-hidden="true" className="animate-boot-cursor h-[11px] w-[7px] shrink-0 bg-signal-oxide" />
            <span className="ml-auto text-right">Nothing publishes while Rally is offline</span>
          </div>
        </div>
      </div>

      <div className={`${stripClass} justify-between`}>
        <span className="flex items-center gap-6">
          <span>Autonomy · propose &amp; wait</span>
          <span>Queue held</span>
        </span>
        <span>If this takes more than 20s, Rally shows the connection error instead</span>
      </div>
    </m.div>
  )
}

const subscribeToRouter = (onChange: () => void): (() => void) => router.subscribe(onChange)
const isRouterInitialized = (): boolean => router.state.initialized

// Boot is over when Clerk has resolved the session and the router has finished its initial data
// load — before both, RouterProvider renders nothing and the page is a bare void ground.
export function AppBoot() {
  const { isLoaded } = useAuth()
  // Frozen at mount: the router hasn't navigated yet, so this is still the URL the redirect
  // landed on.
  const label = useMemo(() => bootLabel(window.location.search), [])
  const routerInitialized = useSyncExternalStore(subscribeToRouter, isRouterInitialized, isRouterInitialized)
  const phase = useAppBoot(isLoaded && routerInitialized)

  return <AppBootScreen phase={phase} label={label} />
}
